import React, { useEffect, useState } from 'react';

import { LogGeralDetalhe, LogGeralItem, obterLogGeralDe, ROTULO_DO_TIPO_DE_ATOR, SistemaDoLog } from '../../../../api/auditLogsApi';
import ModalSync from '../../../../components/sync-painel/ModalSync';
import { notify } from '../../../../shared/feedback/notify';
import { descreverQuem, formatarQuando } from './formatacao';
import '../../../../shared/feedback/erro-de-tela.css';
import '../../../../shared/ui/badge-status.css';

interface Props {
  /** De qual sistema é o registro (o SOAC ou um satélite). */
  sistema: SistemaDoLog;
  id: string;
  onClose: () => void;
  /** "Ver histórico deste registro": o chamador aplica o filtro por registro, sem período. */
  onVerHistorico: (entidade: string, entidadeId: string) => void;
}

const Par: React.FC<{ rotulo: string; valor: React.ReactNode }> = ({ rotulo, valor }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--tema-linha, #eee)' }}>
    <span className="admin-label" style={{ minWidth: 150 }}>
      {rotulo}
    </span>
    <span style={{ wordBreak: 'break-word' }}>{valor ?? '-'}</span>
  </div>
);

const Json: React.FC<{ valor: unknown }> = ({ valor }) => (
  <pre
    style={{
      background: 'var(--tema-fundo-2, #f6f7f9)',
      padding: 12,
      borderRadius: 8,
      overflowX: 'auto',
      fontSize: 12,
      maxHeight: 280,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}
  >
    {JSON.stringify(valor, null, 2)}
  </pre>
);

const texto = (v: unknown) => (v === null || v === undefined ? '(vazio)' : typeof v === 'string' ? v : JSON.stringify(v));

/**
 * Detalhe de um registro do log geral (transversal/at-91). Abrir o detalhe GERA log — é o que o
 * Ambev também faz: quem consultou o rastro de quem.
 *
 * ⚠️ A tela nunca mascara nada por conta própria: os dados enviados chegam já sanitizados pelo
 * servidor, e é lá que a regra vive.
 */
const DetalheLogGeralModal: React.FC<Props> = ({ sistema, id, onClose, onVerHistorico }) => {
  const [detalhe, setDetalhe] = useState<LogGeralDetalhe | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    obterLogGeralDe(sistema, id)
      .then((d) => vivo && setDetalhe(d))
      .catch((error) => {
        if (!vivo) return;
        setErro(true);
        notify.errorFrom(error, 'Não foi possível abrir este registro do log. Recarregue a página; se persistir, acione a TI.');
      });
    return () => {
      vivo = false;
    };
  }, [sistema, id]);

  return (
    <ModalSync titulo="Registro do log" subtitulo={detalhe ? formatarQuando(detalhe.createdAt) : undefined} onClose={onClose} largura="min(820px, 96vw)">
      {erro && <p className="erro-de-tela">Não foi possível carregar este registro.</p>}
      {!erro && !detalhe && <p className="admin-texto-secundario">Carregando…</p>}
      {detalhe && (
        <>
          <div className="filtro-section">
            <Par rotulo="O que" valor={detalhe.resumo} />
            <Par
              rotulo="Resultado"
              valor={
                <span className={`cilia-badge ${detalhe.sucesso ? 'badge-verde' : 'badge-vermelho'}`}>
                  {detalhe.sucesso ? 'Sucesso' : 'Falha'}
                  {detalhe.statusCode ? ` (${detalhe.statusCode})` : ''}
                </span>
              }
            />
            <Par rotulo="Quem" valor={descreverQuem(detalhe)} />
            <Par rotulo="Tipo de ator" valor={ROTULO_DO_TIPO_DE_ATOR[detalhe.atorTipo]} />
            <Par rotulo="O que" valor={detalhe.resumo} />
            <Par rotulo="Código da ação" valor={detalhe.acao} />
            <Par rotulo="Módulo" valor={detalhe.modulo} />
            <Par rotulo="Registro" valor={detalhe.registro} />
            <Par rotulo="Id do registro" valor={detalhe.entidadeId} />
            <Par rotulo="Rota" valor={detalhe.rota ? `${detalhe.metodo ?? ''} ${detalhe.rota}` : detalhe.tipo === 'NEGOCIO' ? 'Registro interno (fora de requisição)' : null} />
            <Par rotulo="IP" valor={detalhe.ip} />
            <Par rotulo="Navegador" valor={detalhe.userAgent} />
          </div>

          {detalhe.entidadeId && (
            <button
              type="button"
              className="admin-btn"
              onClick={() => onVerHistorico(detalhe.entidade, detalhe.entidadeId as string)}
              style={{ marginBottom: 12 }}
            >
              Ver histórico deste registro
            </button>
          )}

          {detalhe.changes && Object.keys(detalhe.changes).length > 0 && (
            <div className="filtro-section">
              <h3 className="filtro-section-title">Antes e depois</h3>
              {Object.entries(detalhe.changes).map(([campo, m]) => (
                <Par key={campo} rotulo={campo} valor={`${texto(m.antes)}  →  ${texto(m.depois)}`} />
              ))}
            </div>
          )}

          {detalhe.linhasDaMesmaRequisicao.length > 0 && (
            <div className="filtro-section">
              <h3 className="filtro-section-title">O que esta ação gravou</h3>
              {detalhe.linhasDaMesmaRequisicao.map((l: LogGeralItem) => (
                <Par key={l.id} rotulo={l.registro} valor={l.resumo} />
              ))}
            </div>
          )}

          {detalhe.payload !== null && detalhe.payload !== undefined && (
            <div className="filtro-section">
              <h3 className="filtro-section-title">Dados enviados</h3>
              <p className="filtro-field-hint">Senhas, tokens e chaves nunca são gravados. CPF e telefone aparecem mascarados.</p>
              <Json valor={detalhe.payload} />
            </div>
          )}

          {detalhe.reason !== null && detalhe.reason !== undefined && (
            <div className="filtro-section">
              <h3 className="filtro-section-title">Detalhe registrado pelo sistema</h3>
              <Json valor={detalhe.reason} />
            </div>
          )}
        </>
      )}
    </ModalSync>
  );
};

export default DetalheLogGeralModal;
